import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

const OTP_LENGTH = 6;

type OtpInputProps = {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
};

export function OtpInput({ value, onChange, disabled }: OtpInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      inputsRef.current[0]?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  const handleChange = (digit: string, index: number) => {
    if (!/^[0-9]?$/.test(digit)) return;
    const next = [...value];
    next[index] = digit;
    onChange(next);
    if (digit && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .trim()
      .split('')
      .filter((char) => /^[0-9]$/.test(char))
      .slice(0, OTP_LENGTH);

    const next = [...value];
    pasted.forEach((char, i) => {
      next[i] = char;
    });
    onChange(next);

    const nextIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[nextIndex]?.focus();
  };

  return (
    <div className="mb-6 flex justify-start gap-2 sm:gap-3">
      {value.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          disabled={disabled}
          value={digit}
          onChange={(e) => handleChange(e.target.value.replace(/\D/g, '').slice(-1), index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          className="signup-input box-border h-11 w-10 rounded-[9px] border border-[#E2E9E7] bg-white text-center text-lg font-semibold text-[#0F1817] outline-none transition-[border-color,box-shadow] sm:h-12 sm:w-12"
        />
      ))}
    </div>
  );
}

export { OTP_LENGTH };
